import { Injectable } from '@angular/core';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { DateService } from 'src/app/shared/services/date.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from '../../../interfaces/zipCountyStateStructure.interface';
import moment from 'moment';
import { INDIVIDUAL_SMALLGROUP_REGION_ENUM } from '@ptl/account-management';
import { EnrollmentAppStateStructure } from '../../../interfaces/enrollmentAppStateStructure.interface';

enum RATING_ENTITY_ENUM {
  DEFAULT = "DEFAULT",
  HIGHMARK_BLUE_CROSS_BLUE_SHIELD = "HBCBS",
  HOSPITAL_CORPORATION_of_AMERICA = "HCA",
  HIGHMARK_BLUE_SHIELD = "HBS",
  HIGHMARK_BENEFITS_GROUP = "HBG"
}

enum SEP_FLOW_TYPE {
  STANDARD = 'ES',
  TELESALES = 'EP'
}

/**
 * OE (Open Enrollment) form number data, keyed by the plan year being enrolled into.
 * The enrollment year is derived from the effective date's year.
 *
 * childOnlySuffix: used when the enrollment is for a child-only policy. Set this to
 * the same value as suffix when child-only and standard share the same form number,
 * so that the intent is explicit and visible at a glance.
 *
 * To add a new year: add a new numeric key entry to each region in oeFormNumbers.
 */
interface OEFormEntry {
  suffix: string;
  childOnlySuffix: string;
  agentRoleSuffix: string;
}

/**
 * SEP form number data, keyed by the effective date's year.
 * Because SEP effective dates after December 1st roll into the following year,
 * the year key naturally selects the correct form number without any special-period logic.
 *
 * To add a new year: add a new numeric key entry to each region in sepFormNumbers.
 */
interface SEPFormEntry {
  prefix: string;
  standardSuffix: string;
  telesalesSuffix: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormNumberService {

  /**
   * OE prefix by region and rating entity. Prefixes do not change year-over-year.
   * Named rating entity entries that currently match DEFAULT are kept explicitly
   * so they can be diverged independently in the future without structural changes.
   */
  private readonly oePrefixMap: Map<string, Map<string, string>> = new Map([
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.CPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD,          "APP-DP-HBS/C-WEB"],
      [RATING_ENTITY_ENUM.DEFAULT,                       "APP-DP-HBS/C-WEB"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.DELAWARE, new Map([
      [RATING_ENTITY_ENUM.DEFAULT,                        ""]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NENY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD,          "NENY-APP"],
      [RATING_ENTITY_ENUM.DEFAULT,                       "NENY-APP"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP,       "APP-DP-HBG-WEB"],
      [RATING_ENTITY_ENUM.DEFAULT,                       "APP-DP-HBG-WEB"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.SEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP,       "APP-DP-HBG-SEPA-WEB"],
      [RATING_ENTITY_ENUM.DEFAULT,                       "APP-DP-HBG-SEPA-WEB"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WNY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, "WNY-APP"],
      [RATING_ENTITY_ENUM.DEFAULT,                         "WNY-APP"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, "APP-DP-HBCBS-WEB"],
      [RATING_ENTITY_ENUM.HOSPITAL_CORPORATION_of_AMERICA, "APP-DP-HCA/W-WEB"],
      [RATING_ENTITY_ENUM.DEFAULT,                         "APP-DP-HBCBS-WEB"]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WEST_VIRGINA, new Map([
      [RATING_ENTITY_ENUM.DEFAULT,                        ""]
    ])]
  ]);

  /**
   * OE suffix and agent role suffix by region, rating entity, and enrollment year.
   * Named rating entity entries that currently match DEFAULT are kept explicitly
   * so they can be diverged independently in the future without structural changes.
   *
   * ---- Adding a new year ----
   * Add a new [year, { suffix, agentRoleSuffix }] entry under each rating entity for each region.
   */
  private readonly oeFormNumbers: Map<string, Map<string, Map<number, OEFormEntry>>> = new Map([
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.CPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD, new Map<number, OEFormEntry>([
        [2026, { suffix: "-6",    childOnlySuffix: "-6",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-7",    childOnlySuffix: "-7",    agentRoleSuffix: "T" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-6",    childOnlySuffix: "-6",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-7",    childOnlySuffix: "-7",    agentRoleSuffix: "T" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.DELAWARE, new Map([
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "",      childOnlySuffix: "",      agentRoleSuffix: "" }],
        [2027, { suffix: "",      childOnlySuffix: "",      agentRoleSuffix: "" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NENY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD, new Map<number, OEFormEntry>([
        [2026, { suffix: "-25-E", childOnlySuffix: "-25-E", agentRoleSuffix: "-P" }],
        [2027, { suffix: "-26-E", childOnlySuffix: "-26-E", agentRoleSuffix: "-P" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-25-E", childOnlySuffix: "-25-E", agentRoleSuffix: "-P" }],
        [2027, { suffix: "-26-E", childOnlySuffix: "-26-E", agentRoleSuffix: "-P" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP, new Map<number, OEFormEntry>([
        [2026, { suffix: "-6",    childOnlySuffix: "-6",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-7",    childOnlySuffix: "-7",    agentRoleSuffix: "T" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-6",    childOnlySuffix: "-6",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-7",    childOnlySuffix: "-7",    agentRoleSuffix: "T" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.SEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP, new Map<number, OEFormEntry>([
        [2026, { suffix: "-2",    childOnlySuffix: "-2",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-3",    childOnlySuffix: "-3",    agentRoleSuffix: "T" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-2",    childOnlySuffix: "-2",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-3",    childOnlySuffix: "-3",    agentRoleSuffix: "T" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WNY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, new Map<number, OEFormEntry>([
        [2026, { suffix: "-25-E", childOnlySuffix: "-25-E", agentRoleSuffix: "-P" }],
        [2027, { suffix: "-26-E", childOnlySuffix: "-26-E", agentRoleSuffix: "-P" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-25-E", childOnlySuffix: "-25-E", agentRoleSuffix: "-P" }],
        [2027, { suffix: "-26-E", childOnlySuffix: "-26-E", agentRoleSuffix: "-P" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, new Map<number, OEFormEntry>([
        [2026, { suffix: "-12",   childOnlySuffix: "-12",   agentRoleSuffix: "T" }],
        [2027, { suffix: "-13",   childOnlySuffix: "-13",   agentRoleSuffix: "T" }],
      ])],
      [RATING_ENTITY_ENUM.HOSPITAL_CORPORATION_of_AMERICA, new Map<number, OEFormEntry>([
        [2026, { suffix: "-6",    childOnlySuffix: "-6",    agentRoleSuffix: "T" }],
        [2027, { suffix: "-7",    childOnlySuffix: "-7",    agentRoleSuffix: "T" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "-12",   childOnlySuffix: "-12",   agentRoleSuffix: "T" }],
        [2027, { suffix: "-13",   childOnlySuffix: "-13",   agentRoleSuffix: "T" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WEST_VIRGINA, new Map([
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, OEFormEntry>([
        [2026, { suffix: "",      childOnlySuffix: "",      agentRoleSuffix: "" }],
        [2027, { suffix: "",      childOnlySuffix: "",      agentRoleSuffix: "" }],
      ])]
    ])]
  ]);

  /**
   * SEP form numbers by region, rating entity, and effective year.
   * Named rating entity entries that currently match DEFAULT are kept explicitly
   * so they can be diverged independently in the future without structural changes.
   *
   * The effective year is taken directly from the effective date. Because SEP
   * effective dates on or after December 1st are always in the following calendar
   * year, no special-period date math is needed here -- the year key does the work.
   *
   * ---- Adding a new year ----
   * Add a new [year, { prefix, standardSuffix, telesalesSuffix }] entry under each rating entity for each region.
   */
  private readonly sepFormNumbers: Map<string, Map<string, Map<number, SEPFormEntry>>> = new Map([
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.CPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BS-SEP",   standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BS-SEP",   standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BS-SEP",   standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BS-SEP",   standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.HOSPITAL_CORPORATION_of_AMERICA, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.SEPA, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BENEFITS_GROUP, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "BCBS-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "BCBS-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.WNY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_CROSS_BLUE_SHIELD, new Map<number, SEPFormEntry>([
        [2026, { prefix: "WNY-SEP",  standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "WNY-SEP",  standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "WNY-SEP",  standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "WNY-SEP",  standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])],
    [INDIVIDUAL_SMALLGROUP_REGION_ENUM.NENY, new Map([
      [RATING_ENTITY_ENUM.HIGHMARK_BLUE_SHIELD, new Map<number, SEPFormEntry>([
        [2026, { prefix: "NENY-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "NENY-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])],
      [RATING_ENTITY_ENUM.DEFAULT, new Map<number, SEPFormEntry>([
        [2026, { prefix: "NENY-SEP", standardSuffix: "-ES-1", telesalesSuffix: "-EP-1" }],
        [2027, { prefix: "NENY-SEP", standardSuffix: "-ES-2", telesalesSuffix: "-EP-2" }],
      ])]
    ])]
  ]);

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private commonUtilityService: CommonUtilityService,
    private dateService: DateService
  ) {}

  generateFormNumber(isSEP: boolean, isChildOnly: boolean = false): string {
    let eApp: EnrollmentAppStateStructure = this.reducerUtilityService.getState('EnrollmentApp');
    let ratingEntity: string;
    let selectedPlan: Product<PlanAttributes, SummaryView>;

    const zipCounty: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const region = zipCounty.county.region as INDIVIDUAL_SMALLGROUP_REGION_ENUM;
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();
    const isAgent = this.commonUtilityService.isAgent();

    const payloadStates = this.reducerUtilityService.getPayloadStates();

    /**
     * Devon: 2025-12-18 - as part of the SEP form number effort, I needed to take into account effective date.
     * However, upon debugging - it seemed that effective date from enrollment never existed (maybe we were lucky).
     * However(2), upon reflection, I'm wondering if there is logic associated w/ pre-populated data that might
     * make it make sense to preserve this accessor. To that end, I've just added a fallback to fetching of
     * effective date from the component reducer.
     */
    const enrollmentData = payloadStates.enrollment;
    const effectiveDate = enrollmentData.effectiveDate || payloadStates.individualMarket.ratingInfo.effectiveDate;
    const effectiveDateAsMoment = this.dateService.getDateAsMoment(effectiveDate);

    try {
      selectedPlan = plansFromState.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment)[0];
    } catch (e) {
      console.error('No plan selected: ', e);
    }

    try {
      ratingEntity = selectedPlan
        ? selectedPlan.attributes.RATING_ENTITY.toUpperCase()
        : eApp.submittedPlan.attributes.RATING_ENTITY.toUpperCase();
    } catch (e) {
      console.error('No rating entity available on plan nor payload', e);
    }

    if (isSEP) {
      return this.generateSEPFormNumber(region, ratingEntity, effectiveDateAsMoment, isAgent);
    }

    return this.generateOEFormNumber(region, ratingEntity, effectiveDateAsMoment, isAgent, isChildOnly);
  }

  /**
   * Builds a SEP form number.
   *
   * The effective year is used directly as the lookup key. SEP effective dates
   * on or after December 1st are always in the following calendar year, so the
   * year key naturally resolves to the correct entry without any date-range math.
   */
  private generateSEPFormNumber(
    region: INDIVIDUAL_SMALLGROUP_REGION_ENUM,
    ratingEntity: string,
    effectiveDateAsMoment: moment.Moment,
    isAgent: boolean
  ): string {
    const effectiveYear = effectiveDateAsMoment.year();

    const entry =
      this.sepFormNumbers.get(region)?.get(ratingEntity)?.get(effectiveYear) ??
      this.sepFormNumbers.get(region)?.get(RATING_ENTITY_ENUM.DEFAULT)?.get(effectiveYear);

    if (!entry) {
      console.error(`No SEP form number configured for region=${region}, year=${effectiveYear}`);
      return "";
    }

    const suffix = isAgent ? entry.telesalesSuffix : entry.standardSuffix;
    return `${entry.prefix}${suffix}`;
  }

  /**
   * Builds an OE (Open Enrollment) form number.
   *
   * The enrollment year is taken from the effective date's year, which for OE
   * enrollments is always the plan year being purchased.
   */
  private generateOEFormNumber(
    region: INDIVIDUAL_SMALLGROUP_REGION_ENUM,
    ratingEntity: string,
    effectiveDateAsMoment: moment.Moment,
    isAgent: boolean,
    isChildOnly: boolean = false
  ): string {
    const enrollmentYear = effectiveDateAsMoment.year();

    const prefix =
      this.oePrefixMap.get(region)?.get(ratingEntity) ??
      this.oePrefixMap.get(region)?.get(RATING_ENTITY_ENUM.DEFAULT) ??
      "";

    const entry =
      this.oeFormNumbers.get(region)?.get(ratingEntity)?.get(enrollmentYear) ??
      this.oeFormNumbers.get(region)?.get(RATING_ENTITY_ENUM.DEFAULT)?.get(enrollmentYear);

    if (!entry) {
      console.error(`No OE form number configured for region=${region}, year=${enrollmentYear}`);
      return prefix;
    }

    const suffix = isChildOnly ? entry.childOnlySuffix : entry.suffix;
    const roleSuffix = isAgent ? entry.agentRoleSuffix : "";
    return `${prefix}${suffix}${roleSuffix}`;
  }
}
